import React from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

interface TermsAndConditionsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TermsAndConditionsSheet: React.FC<TermsAndConditionsSheetProps> = ({ isOpen, onOpenChange }) => {
  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[96vh] flex flex-col p-0 rounded-t-2xl border-t border-border [&>button]:hidden"
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 pt-4 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="w-10" />
            <h2 className="text-lg font-semibold text-foreground">Terms and Conditions</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-5 text-sm text-muted-foreground">
          <p className="text-xs text-muted-foreground/60">Last Updated: March 25, 2026</p>

          <p>
            Welcome to Fork It! These Terms and Conditions ("Terms") govern your use of the Fork It mobile application
            and website (https://forkit.uk) (collectively, the "App"), operated by Arab Western Ltd ("we," "us," or "our").
          </p>
          <p>
            By creating an account or otherwise accessing or using the App, you agree to be bound by these Terms. If you
            do not agree to these Terms, you may not use the App.
          </p>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">1. Description of Service</h3>
            <p>
              Fork It is a community-driven platform where users discover, rate, and share local food dishes via photos
              ("Snaps"), videos ("Drools"), and reviews. We provide the platform; the content is generated by the community.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">2. User Accounts</h3>
            <ul className="space-y-1 list-disc list-inside">
              <li>To use certain features of the App, you must register for an account.</li>
              <li>You must provide accurate and complete information.</li>
              <li>You are responsible for safeguarding your login credentials.</li>
              <li>You must notify us immediately of any unauthorized use of your account.</li>
              <li>We reserve the right to suspend or terminate your account at our discretion, especially in cases of Terms violations.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">3. User-Generated Content (UGC) &amp; Rules of Conduct</h3>
            <p>
              As a community-driven app, you may post photos, videos, ratings, comments, and other content ("User Content").
              You retain ownership of your User Content, but you grant Arab Western Ltd a worldwide, non-exclusive,
              royalty-free, transferable license to use, display, reproduce, and distribute your User Content within the
              App and for promotional purposes.
            </p>
            <p className="font-medium text-foreground">Zero Tolerance for Objectionable Content:</p>
            <p>To maintain a safe and authentic community, you agree that you will NOT post User Content that:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Is defamatory, abusive, harassing, threatening, or constitutes hate speech.</li>
              <li>Contains explicit, pornographic, violent, or otherwise offensive material.</li>
              <li>Infringes on the intellectual property rights (e.g., copyright, trademarks) of others.</li>
              <li>Constitutes spam, fake reviews, or undisclosed paid promotions (astroturfing).</li>
              <li>Contains private or personal information of others without their consent.</li>
            </ul>
            <p className="font-medium text-foreground">Content Moderation:</p>
            <p>
              We actively monitor the platform. We reserve the right to review, flag, modify, or remove any User Content
              at our sole discretion, at any time, without notice.
            </p>
            <p className="font-medium text-foreground">Reporting and Blocking:</p>
            <p>
              Users have the ability to flag/report inappropriate content and block abusive users within the App. If you
              are found to be violating these Terms, Arab Western Ltd reserves the right to immediately ban your account
              and remove your content.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">4. Acceptable Use</h3>
            <p>You agree not to engage in any of the following prohibited activities:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Using automated systems (bots, spiders, scrapers) to extract data from the App.</li>
              <li>Interfering with or disrupting the integrity or performance of the App or its servers.</li>
              <li>Attempting to reverse-engineer, decompile, or hack the App.</li>
              <li>Using the App for any illegal or unauthorized purpose under UK law.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">5. Intellectual Property</h3>
            <p>
              Excluding User Content, all materials within the App, including the Fork It name, logos, design, UI/UX,
              text, graphics, and software, are the intellectual property of Arab Western Ltd and are protected by
              copyright and trademark laws. You may not use our intellectual property without our prior written consent.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">6. Disclaimers</h3>
            <p className="font-medium text-foreground">Food and Allergen Disclaimer:</p>
            <p>
              Fork It relies on crowd-sourced data. We do not verify the accuracy of dish ingredients, dietary claims
              (e.g., vegan, gluten-free), or prices. Always verify directly with the restaurant regarding severe allergies
              or dietary restrictions. We are not liable for any adverse reactions, illnesses, or pricing discrepancies.
            </p>
            <p className="font-medium text-foreground">"As Is" Service:</p>
            <p>
              The App is provided on an "AS IS" and "AS AVAILABLE" basis. We make no warranties that the App will be
              error-free, secure, or uninterrupted.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">7. Limitation of Liability</h3>
            <p>
              To the maximum extent permitted by applicable law, Arab Western Ltd shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether
              incurred directly or indirectly, resulting from:
            </p>
            <ul className="space-y-1 list-disc list-inside">
              <li>your use or inability to use the App;</li>
              <li>any conduct or content of any third party on the App;</li>
              <li>any unauthorized access to your data.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">8. Governing Law and Jurisdiction</h3>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of England and Wales. Any
              disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction
              of the courts of England and Wales.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">9. Changes to Terms</h3>
            <p>
              We may modify these Terms at any time. We will notify users of significant changes by updating the "Last
              Updated" date or via an in-app notification. Your continued use of the App after such changes constitutes
              your acceptance of the new Terms.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">10. Contact Information</h3>
            <p>If you have any questions about these Terms, please contact us at:</p>
            <p>
              Arab Western Ltd<br />
              Email: mediaforkit@gmail.com<br />
              Website: https://forkit.uk
            </p>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};
